import { useState, memo } from 'react';
import {
  Toolbar,
  Button,
  Menu,
  MenuItem,
  Stack,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const ReportToolbar = ({
  onRefresh,
  onMemorize,
  onReset,
  onExportExcel,
  onExportPdf,
  onCustomize,
  permissions = {},
}) => {
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);

  const openSettingsMenu = Boolean(settingsAnchorEl);
  const openExportMenu = Boolean(exportAnchorEl);

  const handleSettingsClick = (event) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const handleAction = (action, closeMenu) => {
    action?.();
    closeMenu?.();
  };

  // Common button style
  const buttonSx = {
    backgroundColor: '#2782CE',
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 500,
    borderRadius: 0.5,
    height: '28px',
    minHeight: '28px',
    padding: '4px 8px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#2782CE',
    },
  };

  // Dropdown Menu Paper style (matches our CustomSelect / Dropdown UI)
  const menuPaperSx = {
    borderRadius: 0,
    border: '1px solid #d8d8d8',
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
    mt: 0.5,
    minWidth: 100,
    '& .MuiList-root': {
      p: 0,
    },
  };

  // Dropdown MenuItem style (matches our CustomSelect / Dropdown UI)
  const menuItemSx = {
    fontSize: '0.7rem',
    fontWeight: 500,
    lineHeight: '18px',
    py: '5px',
    px: '12px',
    color: '#111827',
    borderBottom: '1px solid #d8d8d8',
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: '#2f5d8a !important',
      color: '#ffffff',
    },
    '&.Mui-selected': {
      backgroundColor: '#2f5d8a !important',
      color: '#ffffff',
    },
  };

  return (
    <Toolbar
      sx={{
        justifyContent: 'flex-end',
        p: '4px !important',
        mb: 0,
        minHeight: { xs: 'auto' },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
      >
        {/* Refresh */}
        <Button
          onClick={onRefresh}
          sx={buttonSx}
        >
          Refresh
        </Button>

        {/* Settings */}
        <Button
          aria-controls={openSettingsMenu ? 'settings-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={openSettingsMenu ? 'true' : undefined}
          onClick={handleSettingsClick}
          endIcon={<ArrowDropDownIcon />}
          sx={buttonSx}
        >
          Settings
        </Button>

        <Menu
          id="settings-menu"
          anchorEl={settingsAnchorEl}
          open={openSettingsMenu}
          onClose={handleSettingsClose}
          slotProps={{
            paper: {
              sx: menuPaperSx,
            },
          }}
        >
          <MenuItem
            onClick={() =>
              handleAction(onMemorize, handleSettingsClose)
            }
            sx={menuItemSx}
          >
            Memorize
          </MenuItem>

          <MenuItem
            onClick={() =>
              handleAction(onReset, handleSettingsClose)
            }
            sx={menuItemSx}
          >
            Reset
          </MenuItem>
        </Menu>

        {/* Export */}
        <Button
          aria-controls={openExportMenu ? 'export-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={openExportMenu ? 'true' : undefined}
          onClick={handleExportClick}
          endIcon={<ArrowDropDownIcon />}
          sx={buttonSx}
        >
          Export to
        </Button>

        <Menu
          id="export-menu"
          anchorEl={exportAnchorEl}
          open={openExportMenu}
          onClose={handleExportClose}
          slotProps={{
            paper: {
              sx: menuPaperSx,
            },
          }}
        >
          <MenuItem
            onClick={() =>
              handleAction(onExportExcel, handleExportClose)
            }
            sx={menuItemSx}
          >
            Excel
          </MenuItem>

          <MenuItem
            onClick={() =>
              handleAction(onExportPdf, handleExportClose)
            }
            sx={menuItemSx}
          >
            PDF
          </MenuItem>
        </Menu>

        {/* Customize */}
        <Button
          onClick={onCustomize}
          sx={buttonSx}
        >
          Customize
        </Button>
      </Stack>
    </Toolbar>
  );
};

export default memo(ReportToolbar);
